import React from 'react';
import {
  Box,
  Grid,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PrimaryOutlinedButton } from 'components';
import {
  CertificateTemplate,
  ICertificate,
} from '../../../../interface';
import CertificateModal from '../certificate-modal';
import {
  buildCertificateRenderRequest,
  getCertificatePreviewFrameHeight,
  getCertificateSummary,
  renderCertificatePreviewHtmlViaService,
} from '../certificate-modal/utils';

interface ViewCertificateProps {
  formTitle: string,
  certificate: ICertificate | null | undefined;
  handleCertificate: (certificate: ICertificate) => void;
  handleCertificateRemove: () => void;
  sessionTitle?: string;
  moduleTitles?: string[];
  enterprise?: {
    entLogo?: string | null;
    logo?: string | null;
    name?: string | null;
  } | null;
}

const ViewCertificate = ({
  certificate,
  handleCertificate,
  handleCertificateRemove,
  formTitle,
  sessionTitle,
  moduleTitles,
  enterprise,
}: ViewCertificateProps) => {
  const hasCertificate = Boolean(certificate?.title);
  const [thumbnail, setThumbnail] = React.useState({ html: '', error: '' });
  React.useEffect(() => {
    let ignore = false;

    const renderThumbnail = async () => {
      if (!certificate) {
        if (!ignore) {
          setThumbnail({ html: '', error: '' });
        }
        return;
      }

      try {
        const request = buildCertificateRenderRequest({
          certificate,
          enterprise,
          sessionTitle,
          moduleTitles,
        });
        const html = await renderCertificatePreviewHtmlViaService(request);
        if (!ignore) {
          setThumbnail({ html, error: '' });
        }
      } catch (error) {
        if (!ignore) {
          setThumbnail({
            html: '',
            error: error instanceof Error ? error.message : 'Failed to render certificate preview',
          });
        }
      }
    };

    renderThumbnail();
    return () => {
      ignore = true;
    };
  }, [certificate, enterprise, moduleTitles, sessionTitle]);

  const previewFrameHeight = getCertificatePreviewFrameHeight(certificate?.template);
  const previewRenderHeight = certificate?.template === CertificateTemplate.CERT_TEMPLATE_1
    ? Math.max(previewFrameHeight, 1720)
    : Math.max(previewFrameHeight, 1040);
  const previewFirstPageHeight = certificate?.template === CertificateTemplate.CERT_TEMPLATE_1
    ? Math.floor(previewRenderHeight / 2)
    : previewRenderHeight;
  const previewScale = 0.15;
  const previewWidth = Math.round(1122 * previewScale);
  const previewHeight = Math.round(previewFirstPageHeight * previewScale);
  const hasThumbnail = !thumbnail.error && Boolean(thumbnail.html);

  return (
    <div>
      {!hasCertificate ? (
        <CertificateModal
          add
          formTitle={formTitle}
          certificate={certificate}
          setCertificate={handleCertificate}
          sessionTitle={sessionTitle}
          moduleTitles={moduleTitles}
          enterprise={enterprise}
        />
      ) : (
        <Box
          sx={{
            border: '1px solid #d9e2ec',
            borderRadius: 2,
            p: 2,
            mb: 2,
            backgroundColor: '#fff',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {hasThumbnail ? (
              <Grid item xs={12} md={4} lg={3} display="flex" justifyContent="center">
                <Box
                  sx={{
                    border: '1px solid #d9e2ec',
                    borderRadius: '12px',
                    background: '#e9eef5',
                    p: 1,
                    overflow: 'hidden',
                    width: `${previewWidth + 16}px`,
                  }}
                >
                  <Box
                    sx={{
                      width: `${previewWidth}px`,
                      height: `${previewHeight}px`,
                      margin: '0 auto',
                    }}
                  >
                    <iframe
                      title={`${certificate?.title} thumbnail preview`}
                      srcDoc={thumbnail.html}
                      style={{
                        width: '1122px',
                        height: `${previewRenderHeight}px`,
                        border: 0,
                        transform: `scale(${previewScale})`,
                        transformOrigin: 'top left',
                        pointerEvents: 'none',
                        background: '#fff',
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            ) : null}

            <Grid
              item
              xs={12}
              md={hasThumbnail ? 8 : 12}
              lg={hasThumbnail ? 9 : 12}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography variant="h4">{certificate?.title}</Typography>
                  <Typography variant="body2">{getCertificateSummary(certificate)}</Typography>
                </Box>
                <Box sx={{
                  display: 'flex', flexDirection: 'row', gap: 1.5, mt: 2,
                }}
                >
                  <CertificateModal
                    add={false}
                    formTitle={formTitle}
                    certificate={certificate}
                    setCertificate={handleCertificate}
                    sessionTitle={sessionTitle}
                    moduleTitles={moduleTitles}
                    enterprise={enterprise}
                  />
                  <PrimaryOutlinedButton
                    onClick={handleCertificateRemove}
                    startIcon={<CloseIcon />}
                  >
                    Remove
                  </PrimaryOutlinedButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </div>
  );
};

ViewCertificate.defaultProps = {
  sessionTitle: '',
  moduleTitles: [],
  enterprise: null,
};

export default ViewCertificate;
